import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImageUp, Link2, ImageOff } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export default function FixMatchDialog({ card, onUpdated, trigger, triggerTestId = "fix-match-trigger" }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const finish = (updated, message) => {
    onUpdated?.(updated);
    toast.success(message);
    setOpen(false);
    setUrl("");
  };

  const handleImportUrl = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const res = await api.post(`/cards/${card.id}/reference-image-url`, { url: url.trim() });
      finish(res.data, "Match image set from your link");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't import that image. Check the link and try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleFileUpload = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setBusy(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(`/cards/${card.id}/reference-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      finish(res.data, "Your image is now the match image");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveMatch = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/cards/${card.id}/remove-match`);
      finish(res.data, "Match removed — your scan is now the default image");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove the match. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={triggerTestId}
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-xs text-[#A78BFA] hover:text-white transition-colors"
      >
        {trigger}
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="fix-match-dialog" className="bg-[#111113] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Fix match image</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <p className="text-xs text-zinc-400 mb-2">
                Paste an image link — e.g. on an eBay listing you found, right-click the card photo
                and choose <span className="text-zinc-200">"Copy image address"</span>.
              </p>
              <div className="flex gap-2">
                <Input
                  data-testid="fix-match-url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleImportUrl()}
                  placeholder="https://i.ebayimg.com/images/..."
                  className="bg-black/30 border-white/10 text-sm flex-1"
                />
                <Button
                  data-testid="fix-match-import-button"
                  onClick={handleImportUrl}
                  disabled={busy || !url.trim()}
                  size="sm"
                  className="bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full shrink-0"
                >
                  <Link2 className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Working..." : "Use link"}
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-600">
              <span className="flex-1 h-px bg-white/10" /> or <span className="flex-1 h-px bg-white/10" />
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                data-testid="fix-match-upload-input"
                className="hidden"
                onChange={(e) => { handleFileUpload(e.target.files); e.target.value = ""; }}
              />
              <Button
                data-testid="fix-match-upload-button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                size="sm"
                variant="outline"
                className="border-white/20 hover:bg-white/5 rounded-full"
              >
                <ImageUp className="w-3.5 h-3.5 mr-1.5" /> Upload an image file
              </Button>
              {card.reference_image_url && (
                <Button
                  data-testid="fix-match-remove-button"
                  onClick={handleRemoveMatch}
                  disabled={busy}
                  size="sm"
                  variant="outline"
                  className="border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded-full"
                >
                  <ImageOff className="w-3.5 h-3.5 mr-1.5" /> Remove match — use my scan
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
